<script>
    import { LayerCake, Html } from "layercake";
    import { format } from "d3-format";
    import { onMount } from "svelte";
    import { colorScale, formatRemainsOf } from "./helpers.js";

    import BarcodeChart from "../_components/BarcodeChart.svelte";
    import Tooltip from "../_components/Tooltip.svelte";

    import guff from "dataset:../_data/guff.aml";

    export let ctx;

    let event;
    let hideTooltip = false;

    let padding = { right: 1 };

    let institutions = [];
    let institution;
    let color;
    let ready = false;

    onMount(async () => {
        ({ default: institutions } = await import(
            // @ts-expect-error
            "dataset:../_data/institutions/entities.js"
        ));
        institutions.map((i) => {
            i.pending = +i.pending;
            i.pct_of_total = +i.pct_of_total;
        });

        institution = institutions.filter((i) => i["slug"] == ctx["slug"])[0];

        color = colorScale(institution.pct_of_total);
        ready = true;
    });

    let formatTickPct = (d) => format(".0%")(d);
    let formatTickNum = (d) => format(",")(d);

    let pluralize = function (word, value) {
        if (+value > 1 || +value == 0) {
            return word + "s";
        } else {
            return word;
        }
    };
</script>

<div class="chart-container">
    {#if ready}
        <LayerCake
            {padding}
            data={institutions}
            x="pct_of_total"
            z="pending"
            zNice={true}
            ssr={true}
        >
            <Html>
                <div class="subchart-hed">
                    {@html guff.compares_subhed2}
                </div>
                <BarcodeChart
                    {color}
                    {institution}
                    labelWidth={185}
                    type={"not_returned"}
                    formatTick={formatTickNum}
                    axisVar={"z"}
                    bind:hideTooltip
                    textStringPreceding={guff.progress_pending0 + " "}
                    textString={pluralize(
                        guff.compares_label2a,
                        institution.pending
                    ) +
                        " " +
                        guff.compares_label2b}
                    on:message={(evt) => (event = evt)}
                />

                <div class="subchart-hed" style="padding-top: 0.75rem;">
                    {@html guff.compares_subhed1}
                </div>
                <BarcodeChart
                    {color}
                    {institution}
                    labelWidth={160}
                    type={"pct_returned"}
                    formatTick={formatTickPct}
                    axisVar={"x"}
                    bind:hideTooltip
                    textString={guff.compares_label1}
                    on:message={(evt) => (event = evt)}
                />
            </Html>

            <Html pointerEvents={false}>
                <Tooltip evt={event} let:detail {hideTooltip}>
                    <!-- For the tooltip, do another data join because the hover event only has the data from the geography data -->
                    {@const tooltipData = detail.props}

                    <div class="tooltip-content">
                        <div class="tooltip-header">
                            <svg
                                class="institution-icon"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                ><!--! Font Awesome Pro 6.2.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path
                                    d="M240.1 4.2c9.8-5.6 21.9-5.6 31.8 0l171.8 98.1L448 104l0 .9 47.9 27.4c12.6 7.2 18.8 22 15.1 36s-16.4 23.8-30.9 23.8H32c-14.5 0-27.2-9.8-30.9-23.8s2.5-28.8 15.1-36L64 104.9V104l4.4-1.6L240.1 4.2zM64 224h64V416h40V224h64V416h48V224h64V416h40V224h64V420.3c.6 .3 1.2 .7 1.8 1.1l48 32c11.7 7.8 17 22.4 12.9 35.9S494.1 512 480 512H32c-14.1 0-26.5-9.2-30.6-22.7s1.1-28.1 12.9-35.9l48-32c.6-.4 1.2-.7 1.8-1.1V224z"
                                /></svg
                            >
                            <span class="tooltip-title"
                                >{tooltipData.main_institution_short_name}</span
                            >
                        </div>
                        <div>
                            {#if tooltipData.type == "pct_returned"}
                                {guff.map_tooltip_institution1b}
                                <span
                                    class="tooltip-pct-complete"
                                    style="background: {colorScale(
                                        tooltipData.pct_of_total
                                    )};"
                                >
                                    {Math.round(
                                        tooltipData.pct_of_total * 100
                                    )}%</span
                                >
                                of the
                                {@html formatRemainsOf(
                                    tooltipData.total,
                                    false,
                                    false,
                                    false,
                                    true
                                )}
                                {guff.map_tooltip_institution1c2}
                            {:else}
                                {guff.compare_tooltip_not_return} the
                                {@html formatRemainsOf(
                                    tooltipData.pending,
                                    true,
                                    false,
                                    false,
                                    true
                                )}.
                            {/if}
                        </div>
                    </div>
                </Tooltip>
            </Html>
        </LayerCake>
    {/if}
</div>

<style>
    .chart-container {
        width: 100%;
        height: 100%;
    }

    .subchart-hed {
        font-size: var(--scale-2);
        color: var(--font-gray);
        line-height: var(--scale-1);
    }

    .tooltip-content {
        padding: var(--spacing-1);
    }

    .institution-icon {
        width: 0.7rem;
        height: 0.7rem;
        display: inline;
        position: relative;
        top: 1px;
        margin-right: 2px;
    }
    .tooltip-title {
        font-weight: 600;
        font-size: var(--scale-1);
        line-height: 1rem;
    }

    .tooltip-pct-complete {
        padding: 2px 4px;
        color: white;
        font-weight: 600;
    }
</style>
